import * as _ from 'lodash';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { formatCurrency } from '@angular/common';
import { first } from 'rxjs';
import { DateTime } from 'luxon';
import { MtxDrawer } from '@ng-matero/extensions/drawer';
import { HelpersService } from '@app/shared/services/helpers.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { AdminScopes, AdminUser, UserService } from '@app/shared/services/user.service';
import { RemoveSurchargesDrawerComponent } from './drawers/remove-surcharges/remove-surcharges-drawer.component';
import {
  Animations,
  BillingTypes,
  BillingTypesVerbose,
  DefaultTimezone,
  ReconProviderItems,
  ReconciliationFilterStatus,
  ReconciliationFilterStatusVerbose,
  ShipmentChargeStatus,
  ShippingProviderNames,
  ShippingProviders
} from '../../../../shared/constants';
import { AdjustmentsService } from '../../../../shared/services/adjustments.service';
import { ProvidersService } from '../../../../shared/services/providers.service';
import { environment } from '../../../../../environments/environment';
import { ShipmentsService } from '../../../../shared/services/shipments.service';
import { getUniuniRoutingBadge as resolveUniuniRoutingBadge } from '../../../../shared/utils/uniuni-routing-badge.util';
import {
  ModalConfirmationComponent
} from '../../../../shared/components/modal-confirmation/modal-confirmation.component';
import {
  ModalsAssignOtherToChargeComponent
} from './modals/assign-other-to-charge/modals-assign-other-to-charge.component';
import { ModalsBatchAdjustmentsComponent } from './modals/batch-adjustments/modals-batch-adjustments.component';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';

// Synthetic /reconciliation-only courier sub-filters. PostNL-routed and UniUni-direct shipments
// share provider_type 'uniuni', so these ids never reach the backend as a provider value — they
// map to provider 'uniuni' plus a uniuni_routing query param. Kept component-local: the other
// consumers of the shared ReconProviderItems call backends that do not understand the routing
// param (and /unknown-tracking has no shipment metadata to route on at all).
export const UNIUNI_POSTNL_FILTER_ID = 'uniuni-postnl';
export const UNIUNI_DIRECT_FILTER_ID = 'uniuni-direct';

// Display labels for the three /reconciliation-local UniUni entries. The plain "uniuni" entry
// (shared with other dropdowns as just "UniUni") sends no uniuni_routing param and so returns
// the union of both feeds — labelled "(all)" here so it isn't mistaken for a duplicate of
// "(direct)" now that PostNL-routed shipments exist. UI-local labels, not shipment vocabulary,
// so they live here rather than in ShippingProviderNames.
export const UNIUNI_ALL_LABEL = 'UniUni (all)';
export const UNIUNI_DIRECT_LABEL = 'UniUni (direct)';

@Component({
  standalone: false,
  selector: 'app-reconciliation',
  templateUrl: './reconciliation.component.html',
  styleUrls: ['./reconciliation.component.scss'],
  animations: [Animations.ReconExpandAnimation],
})
export class ReconciliationComponent implements OnInit {
  public loading: boolean = true;
  public user: AdminUser;

  public defaultTimezone = DefaultTimezone;
  public progress: number = 0;

  private regexOther = /^Other(\s[-(]|$)/;
  private defaultStatus: {
    id: string;
    value: ShipmentChargeStatus | ReconciliationFilterStatus;
    text: string;
  } = {
    id: ReconciliationFilterStatus.NEEDS_ATTENTION,
    value: ReconciliationFilterStatus.NEEDS_ATTENTION,
    text: ReconciliationFilterStatusVerbose.NEEDS_ATTENTION,
  };

  public daterange = {
    start: DateTime.now().minus({ months: 3 }).startOf('day').toJSDate(),
    end: DateTime.now().set({ hour: 23, minute: 59, second: 59, millisecond: 0 }).toJSDate(),
  };

  public range = new FormGroup({
    start: new FormControl<Date | null>(this.daterange.start),
    end: new FormControl<Date | null>(this.daterange.end),
  });
  private orderByValue = 'createdAt';
  public sortDescending: boolean = true;
  public approveAllInProgress: boolean = false;
  public trackingNumber: string = '';
  public companyId: string = '';

  public selectedFilters = {
    fromDate: this.daterange.start,
    toDate: this.daterange.end,
    chargeStatus: this.defaultStatus.value,
    trackingNumber: this.trackingNumber || '',
    provider: 'all',
  };

  public JobPollInterval: number | null;
  public defaultSearchText: string = `Status: ${this.defaultStatus.text}`;
  public filtersText: string = this.defaultSearchText;

  public statusItems = [
    {
      id: ReconciliationFilterStatus.NEEDS_ATTENTION,
      value: ReconciliationFilterStatus.NEEDS_ATTENTION,
      text: ReconciliationFilterStatusVerbose.NEEDS_ATTENTION,
    },
    {
      id: ReconciliationFilterStatus.PENDING_LIGHT,
      value: ReconciliationFilterStatus.PENDING_LIGHT,
      text: ReconciliationFilterStatusVerbose.PENDING_LIGHT,
    },
    {
      id: ShipmentChargeStatus.ADJUSTMENT_PENDING,
      value: ShipmentChargeStatus.ADJUSTMENT_PENDING,
      text: 'Pending / Declined',
    },
    {
      id: ShipmentChargeStatus.ADJUSTMENT_APPROVED,
      value: ShipmentChargeStatus.ADJUSTMENT_APPROVED,
      text: 'Approved',
    },
    {
      id: ShipmentChargeStatus.ADJUSTMENT_DECLINED,
      value: ShipmentChargeStatus.ADJUSTMENT_DECLINED,
      text: 'Declined',
    },
    {
      id: ShipmentChargeStatus.ADJUSTMENT_IGNORED,
      value: ShipmentChargeStatus.ADJUSTMENT_IGNORED,
      text: 'Ignored',
    },
    {
      id: ShipmentChargeStatus.UNKNOWN_REASON,
      value: ShipmentChargeStatus.UNKNOWN_REASON,
      text: 'Unknown',
    },
    {
      id: ShipmentChargeStatus.DO_NOT_BILL_ADJUSTMENTS,
      value: ShipmentChargeStatus.DO_NOT_BILL_ADJUSTMENTS,
      text: 'Not Bill Adjustments',
    },
    {
      id: ReconciliationFilterStatus.ANY,
      value: ReconciliationFilterStatus.ANY,
      text: ReconciliationFilterStatusVerbose.ANY,
    },
  ];

  // Component-local copy of the shared courier list with the two synthetic UniUni routing
  // sub-filters spliced in after the plain "UniUni" entry (which keeps returning both feeds).
  public providerItems: { id: string; value: string; text: string }[] = this.buildProviderItems();

  public readonly approveAllRoutingTooltip =
    'Approve all applies to every UniUni feed — clear the PostNL/direct filter first';

  public searchForm: FormGroup | undefined;
  public companyAdjustments: any = [];
  public companyAdjustmentsCount = 0;
  public paginatedAdjustments: any[] = [];
  public currentPage: number = 1;
  public itemsPerPage: number = 15;
  public pageSizeOptions: number[] = [15, 25, 50, 100];

  // Summary totals (available for all status filters)
  public grandTotalAmount: number | undefined;
  public grandShipmentCount: number | undefined;
  public pageTotalAmount: number = 0;
  public pageShipmentCount: number = 0;

  public displayedColumns: string[] = ['select', 'name', 'email', 'shipments', 'totalAdjustment', 'action'];

  public displayedNestedColumns: string[] = [
    'select',
    'date',
    'provider',
    'paymentType',
    'price',
    'adjustment',
    'adjustmentDetails',
    'adjustmentAction',
  ];

  public step: number = 0;
  public expandedElement = null;
  public expandedShipmentElement = null;

  // Lazy loading for summary mode (ReconciliationFilterStatus.PENDING_LIGHT, ReconciliationFilterStatus.NEEDS_ATTENTION)
  public loadingShipments: Set<number> = new Set();
  public loadedShipments: Map<number, {
    shipments: any[];
    canApproveBatchAdjustments: boolean;
    shipmentsCount: number;
    totalAdjustmentsAmount: number;
  }> = new Map();

  // shipments
  public canEdit: boolean = false;
  public canViewShipmentDetails: boolean = false;
  public canCreateReturns: boolean = false;
  public isDomesticAccShipment: boolean = false;
  public noReferences = true;
  public additionalDetails: any = {
    reference1: '',
    reference2: '',
    additionalNote: '',
  };
  public CDN_URL: string = environment.CDN_URL;
  public rates;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private drawer: MtxDrawer,
    private bannerNotificationsService: BannerNotificationsService,
    private formBuilder: FormBuilder,
    private userService: UserService,
    private logger: LoggerService,
    private helpersService: HelpersService,
    private providersService: ProvidersService,
    private adjustmentsService: AdjustmentsService,
    private shipmentsService: ShipmentsService
  ) {}
  ngOnInit() {
    this.userService
      .getAuthenticatedUser([AdminScopes.VIEW_RECONCILIATION])
      .pipe(first())
      .subscribe(
        (adminUser: AdminUser) => {
          if (adminUser) {
            this.user = adminUser;
            this.canEdit = this.userService.hasAuthScope(this.user, [AdminScopes.MODIFY_RECONCILIATION]);
            this.canCreateReturns = this.userService.hasAuthScope(this.user, [
              AdminScopes.MODIFY_SHIPMENTS_RETURN_LABEL,
            ]);
            this.canViewShipmentDetails = this.userService.hasAuthScope(this.user, [
              AdminScopes.VIEW_SHIPMENTS_DETAILS,
            ]);
          } else {
            this.router.navigate(['login']);
          }
        },
        (err) => {
          this.router.navigate(['home']);
        }
      );

    this.route.paramMap.subscribe((params: ParamMap) => {
      this.companyId = params.get('companyId');
    });

    this.route.queryParams.subscribe((params) => {
      this.currentPage = parseInt(params['page'], 10) || 1;
    });

    this.startPolling();
    this.createForm();
    this.getAdjustments();
  }

  public approveAll(): void {
    const dialogRef = this.dialog.open(ModalConfirmationComponent, {
      width: '600px',
      data: {
        title: `Confirmation`,
        content: 'Are you sure you want to approve all adjustments?',
        confirmBtnLabel: 'Approve all',
        btnColor: 'primary',
      },
    });
    dialogRef.afterClosed().subscribe((isConfirmed) => {
      if (isConfirmed) {
        this.approveAllInProgress = true;
        this.adjustmentsService.triggerApproveAllRecon().subscribe(
          (response) => {
            this.progress = 1;
            this.startPolling(() => {
              this.bannerNotificationsService.success('Approve all adjustments job finished successfully');
              this.loading = true;
              this.updateFiltersText();
              this.getAdjustments(this.generateQueryData());
            });
            this.logger.log('Reconciliation, approve-all successful', response);
          },
          (error) => {
            this.bannerNotificationsService.error('Approve all has already been triggered...');
            this.logger.error('Reconciliation, approve-all error', error);
            this.restartPolling();
          }
        );
      }
    });
  }

  public setStep(index: number) {
    this.step = index;
  }

  public toggleAdjustmentDetails(adjustmentRow: any) {
    const sameRowClicked = this.expandedElement === adjustmentRow;
    const isAdjustmentDetailsExpanded = !!this.expandedElement;

    if (this.step === 0) {
      this.setStep(1);
      this.expandedElement = adjustmentRow;
      this.expandedShipmentElement = null;
    } else if (this.step === 1) {
      if (sameRowClicked) {
        this.expandedElement = null;
        this.setStep(this.step--);
      } else {
        this.expandedElement = adjustmentRow;
        this.expandedShipmentElement = null;
      }
    } else {
      this.setStep(0);
      this.expandedElement = null;
      this.expandedShipmentElement = null;
    }

    // Lazy load shipments if in summary mode (shipments === null)
    if (this.expandedElement === adjustmentRow && adjustmentRow.shipments === null) {
      const companyId = adjustmentRow.account.companyId;

      // Check cache
      if (this.loadedShipments.has(companyId)) {
        const cached = this.loadedShipments.get(companyId);

        // Validate cache is not stale by comparing with current summary data
        const isStale =
          cached.shipmentsCount !== adjustmentRow.shipmentsCount ||
          cached.totalAdjustmentsAmount !== adjustmentRow.totalAdjustmentsAmount;

        if (isStale) {
          // Cache is stale - delete and fetch fresh data
          this.loadedShipments.delete(companyId);
          this.loadShipmentsForCompany(adjustmentRow);
        } else {
          // Cache is valid - restore from cache
          adjustmentRow.shipments = cached.shipments;
          adjustmentRow.canApproveBatchAdjustments = cached.canApproveBatchAdjustments;
          this.setupShipmentsForCompany(adjustmentRow);
        }
      } else {
        // Not in cache - fetch from API
        this.loadShipmentsForCompany(adjustmentRow);
      }
    }
  }

  public approveRecon(companyAdjustment, shipment): void {
    const companyId = companyAdjustment.account.companyId;
    const dialogRef = this.dialog.open(ModalConfirmationComponent, {
      width: '600px',
      data: {
        title: 'Approve Adjustment',
        content:
          `Approve single adjustment of ${formatCurrency(this.getAdjustmentValue(shipment), 'en', '$', 'CAD')}` +
          ` for ${this.getAccountName(companyAdjustment)}?`,
        btnColor: 'primary',
        confirmBtnLabel: 'Approve',
      },
    });
    dialogRef.afterClosed().subscribe((isConfirmed) => {
      if (isConfirmed) {
        this.loading = true;
        this.adjustmentsService
          .approveAdjustment({
            companyId: companyId,
            shipmentIds: [shipment.id],
          })
          .subscribe(
            (res: Response) => {
              this.getAdjustments(this.generateQueryData());
              this.bannerNotificationsService.success('Adjustment approved successfully');
              this.logger.log(`Reconciliation, Approve adjustment shipment with id ${shipment.id}`, res);
            },
            (err: string) => {
              this.bannerNotificationsService.error('Failed to approve adjustment: ');
              this.logger.error('Reconciliation, Approve adjustment error', err);
              this.getAdjustments(this.generateQueryData());
            }
          );
      }
    });
  }

  public ignoreRecon(companyAdjustment, shipment): void {
    const companyId = companyAdjustment.account.companyId;

    const dialogRef = this.dialog.open(ModalConfirmationComponent, {
      width: '600px',
      data: {
        title: 'Ignore Adjustment',
        content: `Ignore adjustment of ${formatCurrency(this.getAdjustmentValue(shipment), 'en', '$', 'CAD')} for ${
          shipment.senderInfo.name
        }?`,
        btnColor: 'warn',
        confirmBtnLabel: 'Ignore',
      },
    });
    dialogRef.afterClosed().subscribe((isConfirmed) => {
      if (isConfirmed) {
        this.loading = true;
        this.adjustmentsService
          .ignoreAdjustment({
            companyId: companyId,
            shipmentIds: [shipment.id],
          })
          .subscribe(
            (res: Response) => {
              this.getAdjustments(this.generateQueryData());
              this.bannerNotificationsService.success('Adjustment ignored successfully');
              this.logger.log(`Reconciliation, Ignore adjustment shipment with id ${shipment.id}`, res);
            },
            (err: Error) => {
              this.bannerNotificationsService.error('Failed to ignore adjustment');
              this.logger.error('Reconciliation, Ignore adjustment error', err);
            }
          );
      }
    });
  }

  public deleteOtherAndRecalculateAdjustment(companyAdjustment, shipment): void {
    const companyId = companyAdjustment.account.companyId;
    let otherReasons: Array<string>;

    for (const adjustmentCharge of shipment.rates.adjustmentCharges) {
      otherReasons = adjustmentCharge.reasons.filter((reason) => this.regexOther.test(reason));
    }

    if (!otherReasons || otherReasons.length === 0 || otherReasons.length > 1) {
      this.bannerNotificationsService.error('Adjustment should have exactly one "Other Surcharge"');
      return;
    }

    const dialogRef = this.dialog.open(ModalConfirmationComponent, {
      width: '600px',
      data: {
        title: 'Confirmation',
        content: `<span>Are you sure you want to remove <b>${otherReasons[0]}</b> from this adjustment?<span>`,
        btnColor: 'primary',
        confirmBtnLabel: 'Confirm',
      },
    });
    dialogRef.afterClosed().subscribe((isConfirmed) => {
      if (isConfirmed) {
        this.loading = true;
        this.adjustmentsService
          .deleteOtherAndRecalculateAdjustment({
            companyId: companyId,
            shipmentId: shipment.id,
          })
          .subscribe(
            (res: Response) => {
              this.getAdjustments(this.generateQueryData());
              this.bannerNotificationsService.success('"Other" surcharge removed from adjustment');
              this.logger.log(`Reconciliation,, Remove "Other" surcharge for shipment with id ${shipment.id}`, res);
            },
            (err: string) => {
              this.bannerNotificationsService.error('Failed to remove "Other" surcharge from adjustment: ' + err);
              this.logger.error('Reconciliation,, Remove "Other" surcharge error', err);
              this.getAdjustments(this.generateQueryData());
            }
          );
      }
    });
  }

  public reassignOther(companyAdjustment, shipment): void {
    const companyId = companyAdjustment.account.companyId;
    let otherReasons: string[];

    for (const adjustmentCharge of shipment.rates.adjustmentCharges) {
      otherReasons = adjustmentCharge.reasons.filter((reason) => this.regexOther.test(reason));
    }

    if (!otherReasons || otherReasons.length === 0 || otherReasons.length > 1) {
      this.bannerNotificationsService.error('Adjustment should have exactly one "Other Surcharge"');
      return;
    }

    if (Number(otherReasons[0].split('$')[1]) < 0) {
      this.bannerNotificationsService.error('"Other Surcharge" should be a positive value');
      return;
    }

    const dialogRef = this.dialog.open(ModalsAssignOtherToChargeComponent, {
      width: '600px',
    });
    dialogRef.afterClosed().subscribe((charge) => {
      if (charge && charge.chargeCode && charge.chargeName) {
        const { chargeCode, chargeName } = charge;
        this.loading = true;
        this.adjustmentsService
          .assignOtherInAdjustment({
            companyId: companyId,
            shipmentId: shipment.id,
            chargeCode,
            chargeName,
          })
          .subscribe(
            (res: Response) => {
              this.getAdjustments(this.generateQueryData());
              this.bannerNotificationsService.success(`"Other" surcharge assigned to ${charge.chargeName}`);
              this.logger.log(
                `Reconciliation, Assign "Other" surcharge to ${charge.chargeName} for shipment with id ${shipment.id}`,
                res
              );
            },
            (err: string) => {
              this.bannerNotificationsService.error(`Failed to assign "Other" surcharge to ${charge.chargeName}` + err);
              this.logger.error(`Reconciliation, Failed to assign "Other" surcharge to ${charge.chargeName}`, err);
              this.getAdjustments(this.generateQueryData());
            }
          );
      }
    });
  }

  public toggleShipmentDetails(shipmentRow: any) {
    const isShipmentDetailsExpanded = !!this.expandedShipmentElement;
    const clickOnSameRow = this.expandedShipmentElement === shipmentRow;

    if (this.step === 1 && isShipmentDetailsExpanded && clickOnSameRow) {
      this.expandedShipmentElement = null;
    } else if (this.step === 1 && ((isShipmentDetailsExpanded && !clickOnSameRow) || !isShipmentDetailsExpanded)) {
      this.expandedShipmentElement = shipmentRow;
      this.setStep(this.step++);
    } else if (this.step === 2 && isShipmentDetailsExpanded && clickOnSameRow) {
      this.expandedShipmentElement = null;
      this.setStep(this.step--);
    } else if (this.step === 2 && ((isShipmentDetailsExpanded && !clickOnSameRow) || !isShipmentDetailsExpanded)) {
      this.expandedShipmentElement = shipmentRow;
    }
  }

  public reset() {
    this.createForm();
    this.getAdjustments();
  }

  public search(reset: boolean = true) {
    this.loading = true;
    this.currentPage = reset ? 1 : this.currentPage;

    // Clear shipments cache on new search
    this.loadedShipments.clear();
    this.loadingShipments.clear();

    const queryData = this.generateQueryData();

    // Sync selectedFilters with current form values to preserve filters after operations
    if (this.searchForm && this.range) {
      this.selectedFilters = {
        fromDate: this.range.value.start || this.daterange.start,
        toDate: this.range.value.end || this.daterange.end,
        chargeStatus: this.searchForm.value.chargeStatus || this.defaultStatus.value,
        trackingNumber: this.searchForm.value.trackingNumber || '',
        provider: this.searchForm.value.provider || 'all',
      };
    }

    this.getAdjustments(queryData);
  }

  /**
   * Lazy load shipments for a single company (used in summary mode)
   */
  private loadShipmentsForCompany(companyAdjustment: any): void {
    const companyId = companyAdjustment.account.companyId;
    this.loadingShipments.add(companyId);

    const query = {
      ...this.generateQueryData(),
      company: companyId,
      charge_status: ShipmentChargeStatus.ADJUSTMENT_PENDING, // Use real status for detail fetch
    };

    this.adjustmentsService.findAllAdjustments(query).subscribe(
      (res: any) => {
        this.loadingShipments.delete(companyId);

        if (res.companyAdjustments && res.companyAdjustments.length > 0) {
          const loadedCompany = res.companyAdjustments[0];

          // Update the companyAdjustment with loaded data
          companyAdjustment.shipments = loadedCompany.shipments || [];
          companyAdjustment.canApproveBatchAdjustments = loadedCompany.canApproveBatchAdjustments;

          // Cache with staleness check values
          this.loadedShipments.set(companyId, {
            shipments: companyAdjustment.shipments,
            canApproveBatchAdjustments: loadedCompany.canApproveBatchAdjustments,
            shipmentsCount: companyAdjustment.shipmentsCount,
            totalAdjustmentsAmount: companyAdjustment.totalAdjustmentsAmount,
          });

          this.setupShipmentsForCompany(companyAdjustment);
        } else {
          companyAdjustment.shipments = [];
        }
      },
      (err: Error) => {
        this.loadingShipments.delete(companyId);
        companyAdjustment.shipments = [];
        this.bannerNotificationsService.error('Failed to load shipment details');
        this.logger.error('Failed to load shipments for company', err);
      }
    );
  }

  public approveBatchRecon(companyAdjustment): void {
    // If shipments not loaded yet, load them first
    if (!companyAdjustment.shipments || companyAdjustment.shipments.length === 0) {
      this.loadShipmentsForCompany(companyAdjustment);

      // Wait for loading to complete, then open modal
      const checkInterval = 100;
      const maxWaitTime = 10000;
      let elapsedTime = 0;

      const checkLoaded = setInterval(() => {
        elapsedTime += checkInterval;

        if (companyAdjustment.shipments && companyAdjustment.shipments.length > 0) {
          clearInterval(checkLoaded);
          // Verify canApproveBatchAdjustments after loading
          if (!companyAdjustment.canApproveBatchAdjustments) {
            this.bannerNotificationsService.error('This company cannot be batch approved (contains unknown/Other charges requiring review)');
            return;
          }
          this.openBatchApprovalModal(companyAdjustment);
        } else if (elapsedTime >= maxWaitTime) {
          clearInterval(checkLoaded);
          this.bannerNotificationsService.error('Failed to load shipment details');
        }
      }, checkInterval);
    } else {
      // Verify canApproveBatchAdjustments before opening modal
      if (!companyAdjustment.canApproveBatchAdjustments) {
        this.bannerNotificationsService.error('This company cannot be batch approved (contains unknown/Other charges requiring review)');
        return;
      }
      this.openBatchApprovalModal(companyAdjustment);
    }
  }

  private openBatchApprovalModal(companyAdjustment): void {
    const companyId = companyAdjustment.account.companyId;

    const returnDialogRef = this.dialog.open(ModalsBatchAdjustmentsComponent, {
      width: '1000px',
      data: {
        title: 'Approve Adjustments',
        template: 'primary',
        action: 'Approve',
        companyAdjustment: this.setupCompanyAdjustmentForBatch(companyAdjustment),
      },
    });

    returnDialogRef.afterClosed().subscribe((shipmentIds) => {
      if (shipmentIds && shipmentIds?.length > 0) {
        this.loading = true;
        this.adjustmentsService
          .approveAdjustment({
            companyId: companyId,
            shipmentIds: shipmentIds,
          })
          .subscribe(
            (res: Response) => {
              this.getAdjustments(this.generateQueryData());
              this.bannerNotificationsService.success(`${shipmentIds.length} Adjustments approved successfully`);
              this.logger.log(
                `Reconciliation, Approved batch adjustments for company ${companyAdjustment.account.companyId}`,
                res
              );
            },
            (err: string) => {
              this.bannerNotificationsService.error('Failed to approve adjustments: ');
              this.logger.error('Reconciliation, Approve adjustment error', err);
              this.getAdjustments(this.generateQueryData());
            }
          );
      }
    });
  }

  public ignoreBatchRecon(companyAdjustment): void {
    // If shipments not loaded yet, load them first
    if (!companyAdjustment.shipments || companyAdjustment.shipments.length === 0) {
      this.loadShipmentsForCompany(companyAdjustment);

      // Wait for loading to complete, then open modal
      const checkInterval = 100;
      const maxWaitTime = 10000;
      let elapsedTime = 0;

      const checkLoaded = setInterval(() => {
        elapsedTime += checkInterval;

        if (companyAdjustment.shipments && companyAdjustment.shipments.length > 0) {
          clearInterval(checkLoaded);
          // Verify canApproveBatchAdjustments after loading
          if (!companyAdjustment.canApproveBatchAdjustments) {
            this.bannerNotificationsService.error('This company cannot be batch ignored (contains unknown/Other charges requiring review)');
            return;
          }
          this.openBatchIgnoreModal(companyAdjustment);
        } else if (elapsedTime >= maxWaitTime) {
          clearInterval(checkLoaded);
          this.bannerNotificationsService.error('Failed to load shipment details');
        }
      }, checkInterval);
    } else {
      // Verify canApproveBatchAdjustments before opening modal
      if (!companyAdjustment.canApproveBatchAdjustments) {
        this.bannerNotificationsService.error('This company cannot be batch ignored (contains unknown/Other charges requiring review)');
        return;
      }
      this.openBatchIgnoreModal(companyAdjustment);
    }
  }

  private openBatchIgnoreModal(companyAdjustment): void {
    const companyId = companyAdjustment.account.companyId;

    const returnDialogRef = this.dialog.open(ModalsBatchAdjustmentsComponent, {
      width: '1000px',
      data: {
        title: 'Ignore Adjustments',
        template: 'warn',
        action: 'Ignore',
        companyAdjustment: this.setupCompanyAdjustmentForBatch(companyAdjustment),
      },
    });

    returnDialogRef.afterClosed().subscribe((shipmentIds) => {
      if (shipmentIds && shipmentIds?.length > 0) {
        this.loading = true;
        this.adjustmentsService
          .ignoreAdjustment({
            companyId: companyId,
            shipmentIds: shipmentIds,
          })
          .subscribe(
            (res: Response) => {
              this.getAdjustments(this.generateQueryData());
              this.bannerNotificationsService.success(`${shipmentIds.length} Adjustments ignored successfully`);
              this.logger.log(
                `Reconciliation, Ignored batch adjustments for company ${companyAdjustment.account.companyId}`,
                res
              );
            },
            (err: Error) => {
              this.bannerNotificationsService.error('Failed to ignore adjustments');
              this.logger.error('Reconciliation, Ignore adjustment error', err);
            }
          );
      }
    });
  }

  public hasRemovableSurcharges(shipment: any): boolean {
    if (!shipment?.rates?.adjustmentCharges) {
      return false;
    }
    // Pending AND previously-declined adjustments are both actionable (matches the legacy
    // remove-Other flow and the recon "pending" view, which returns AP + AD).
    const removableStatuses = [ShipmentChargeStatus.ADJUSTMENT_PENDING, ShipmentChargeStatus.ADJUSTMENT_DECLINED];
    return shipment.rates.adjustmentCharges.some(
      (c: any) => removableStatuses.includes(c.status) && Array.isArray(c.surcharges) && c.surcharges.length > 0
    );
  }

  public openRemoveSurchargesSingle(companyAdjustment: any, shipment: any): void {
    if (!this.canEdit) {
      return;
    }
    this.drawer
      .open(RemoveSurchargesDrawerComponent, {
        width: '720px',
        disableClose: true,
        closeOnNavigation: false,
        data: { mode: 'single', companyAdjustment, shipment },
      })
      .afterDismissed()
      .subscribe((result) => {
        if (result?.refreshNeeded) {
          this.loadedShipments.delete(companyAdjustment.account.companyId);
          this.getAdjustments(this.generateQueryData());
          this.bannerNotificationsService.success('Surcharges removed and adjustment recalculated');
        }
      });
  }

  public openRemoveSurchargesBulk(companyAdjustment: any): void {
    if (!this.canEdit) {
      return;
    }
    if (!companyAdjustment.shipments || companyAdjustment.shipments.length === 0) {
      this.loadShipmentsForCompany(companyAdjustment);

      const checkInterval = 100;
      const maxWaitTime = 10000;
      let elapsedTime = 0;

      const handle = setInterval(() => {
        elapsedTime += checkInterval;
        if (companyAdjustment.shipments && companyAdjustment.shipments.length > 0) {
          clearInterval(handle);
          this._openRemoveSurchargesBulkDrawer(companyAdjustment);
        } else if (elapsedTime >= maxWaitTime) {
          clearInterval(handle);
          this.bannerNotificationsService.error('Failed to load shipment details');
        }
      }, checkInterval);
    } else {
      this._openRemoveSurchargesBulkDrawer(companyAdjustment);
    }
  }

  private _openRemoveSurchargesBulkDrawer(companyAdjustment: any): void {
    this.drawer
      .open(RemoveSurchargesDrawerComponent, {
        width: '960px',
        disableClose: true,
        closeOnNavigation: false,
        data: { mode: 'bulk', companyAdjustment },
      })
      .afterDismissed()
      .subscribe((result) => {
        if (result?.refreshNeeded) {
          this.loadedShipments.delete(companyAdjustment.account.companyId);
          this.getAdjustments(this.generateQueryData());
          this.bannerNotificationsService.success('Surcharges removed and adjustment recalculated');
        }
      });
  }

  public getProviderCode(service) {
    const serviceProvider = service.provider;
    let serviceName = '';
    if (service.name) {
      serviceName = service.name.toLowerCase();
    }
    if (serviceName && serviceName.includes('fedex') && serviceName.includes('ground')) {
      return 'fedex-ground';
    } else if (
      serviceName &&
      serviceName.includes('fedex') &&
      (serviceName.includes('express') ||
        serviceName.includes('overnight') ||
        serviceName.includes('economy') ||
        serviceName.includes('2day'))
    ) {
      return 'fedex-express';
    } else if (serviceProvider === ShippingProviders.RIVO || serviceProvider === ShippingProviders.MULTILEG) {
      if ((service.code || '').toUpperCase().includes('AC') || (service.name || '').toUpperCase().includes('MULTI')) {
        return this.providersService.getAirCanadaImageByServiceName(serviceName);
      } else {
        return serviceProvider ? serviceProvider : null;
      }
    } else {
      return serviceProvider ? serviceProvider : null;
    }
  }

  public getProviderName(provider: ShippingProviders, service?: any): string {
    return this.helpersService.convertShipmentProviderToString(provider, service);
  }

  /**
   * Feed badge for UniUni shipment rows: PostNL-routed shipments carry a structural
   * metadata.isPostnlUniuniShipment === true indicator; anything else (absent, malformed,
   * non-boolean) is UniUni-direct. Non-UniUni rows get no badge.
   * Delegates to the shared util so this and ShipmentsListComponent's opt-in badge (Finance
   * "not invoiced" tabs) stay in lockstep.
   */
  public getUniuniRoutingBadge(shipment: any): 'PostNL' | 'Direct' | null {
    return resolveUniuniRoutingBadge(shipment);
  }

  /**
   * Approve-All is a global bulk action served by a backend client that applies its own
   * date window and no routing filter — running it while the view is narrowed to one UniUni
   * feed would approve both feeds, so the button is disabled instead.
   */
  public isRoutingFilterActive(): boolean {
    return (
      this.selectedFilters.provider === UNIUNI_POSTNL_FILTER_ID ||
      this.selectedFilters.provider === UNIUNI_DIRECT_FILTER_ID
    );
  }

  public getPaymentType(billingType: BillingTypes): BillingTypesVerbose {
    switch (billingType) {
      case BillingTypes.MACHOOL_ACCOUNT:
        return BillingTypesVerbose.MACHOOL_ACCOUNT;
      default:
        return BillingTypesVerbose.CREDIT_CARD;
    }
  }

  public getAdjustmentValue(shipment: any): number {
    return shipment.rates.adjustmentCharges && shipment.rates.adjustmentCharges.length > 0
      ? shipment.rates.adjustmentCharges.map((charge) => charge.amount).reduce((a, b) => a + b)
      : null;
  }

  public getAdjustmentReasons(shipment: any): string {
    let reasons = '';

    shipment.rates.adjustmentCharges.forEach((adjustmentCharge, index, array) => {
      const finalIndex = array.length - 1;
      if (Array.isArray(adjustmentCharge.reasons)) {
        reasons += `${adjustmentCharge.reasons.join(', ')}`;
      } else {
        reasons += adjustmentCharge.reasons.toString();
      }
      if (finalIndex !== index) reasons += ' - ';
    });

    return reasons;
  }

  public getMissingOrUnknownReasonPopoverMessage(shipment: any): string {
    const reasons = this.getAdjustmentReasons(shipment);

    if (_.isEmpty(reasons)) {
      return 'No specified reason found';
    } else {
      return 'Unknown adjustment charge: Other';
    }
  }

  public getAccountName(company): string {
    if (company.account.companyName) {
      return `${company.account.companyName} / ${company.account.firstName} ${company.account.lastName}`.trim();
    } else {
      return `${company.account.firstName} ${company.account.lastName}`.trim();
    }
  }

  public isCompanyLoading(companyAdjustment: any): boolean {
    if (!companyAdjustment || !companyAdjustment.account) {
      return false;
    }
    return this.loadingShipments.has(companyAdjustment.account.companyId);
  }

  private startPolling(callback?: Function): void {
    if (this.JobPollInterval) return;

    this.JobPollInterval = window.setInterval(() => {
      this.adjustmentsService.getApproveAllJobStatus().subscribe(
        (progressResponse) => {
          if (progressResponse.progress > 0) {
            this.approveAllInProgress = true;
          }
          this.logger.log('Reconciliation, approve-all polling successful', progressResponse);
          this.progress = progressResponse.progress;

          if (!progressResponse.isJobActive || progressResponse.progress === 100) {
            clearInterval(this.JobPollInterval);
            this.JobPollInterval = null;
            this.approveAllInProgress = false;

            if (callback) {
              callback();
            }
          }
        },
        (error) => {
          this.logger.error('Reconciliation, approve-all polling error', error);
          this.restartPolling();
        }
      );
    }, 2000);
  }

  private restartPolling(): void {
    clearInterval(this.JobPollInterval);
    this.JobPollInterval = null;
    this.startPolling();
  }

  private getAdjustments(withQuery = false) {
    this.loading = true;

    let query: any = withQuery;
    if (!query) {
      const providerSelection = this.resolveProviderSelection(this.selectedFilters.provider || 'all');
      query = {
        start_date: DateTime.fromJSDate(this.range.value.start).toUTC(),
        end_date: DateTime.fromJSDate(this.range.value.end).toUTC(),
        tracking_number: this.selectedFilters.trackingNumber || this.trackingNumber || '',
        charge_status: this.selectedFilters.chargeStatus || this.defaultStatus.value,
        provider: providerSelection.provider,
        items_per_page: this.itemsPerPage,
        page: this.currentPage,
        order_by_value: this.orderByValue,
        order_by_direction: this.sortDescending ? 'DESC' : 'ASC',
        company: this.companyId || '',
        include_admin_charge_details: true,
        is_admin_call: true,
      };
      if (providerSelection.uniuniRouting) {
        query.uniuni_routing = providerSelection.uniuniRouting;
      }
    }

    this.companyAdjustments = [];
    this.paginatedAdjustments = [];
    this.companyAdjustmentsCount = 0;

    this.adjustmentsService.findAllAdjustments(query).subscribe(
      (res: any) => {
        try {
          this.logger.log('Adjustments, GET All Adjustments', res);
          this.companyAdjustments = res.companyAdjustments || [];
          this.companyAdjustmentsCount = res.companyAdjustmentsCount || 0;

          // Store grand totals (available for all status filters)
          this.grandTotalAmount = res.grandTotalAmount;
          this.grandShipmentCount = res.grandShipmentCount;

          this.setupCompanyAdjustments(this.companyAdjustments);
          this.paginatedAdjustments = this.companyAdjustments;

          // Calculate page totals
          this.calculatePageTotals();
        } catch (error) {
          this.logger.error('Error processing adjustments data', error);
          // Ensure we still set loading to false even if processing fails
        } finally {
          this.loading = false;
        }
      },
      (err: Error) => {
        this.logger.error('Adjustments, GET All Adjustments error', err);
        this.loading = false;
        this.companyAdjustmentsCount = 0;
        this.companyAdjustments = [];
        this.grandTotalAmount = undefined;
        this.grandShipmentCount = undefined;
        this.pageTotalAmount = 0;
        this.pageShipmentCount = 0;
      }
    );
  }

  private setupCompanyAdjustments(companyAdjustments): void {
    this.companyAdjustments.map((companyAdjustment: any) => {
      if (companyAdjustment) {
        companyAdjustment.name = this.getAccountName(companyAdjustment);

        // If canApproveBatchAdjustments not provided by backend (summary mode),
        // infer it: companies without unknown/Other charges can be batch approved
        if (companyAdjustment.canApproveBatchAdjustments === undefined) {
          companyAdjustment.canApproveBatchAdjustments = !companyAdjustment.hasUnknownReasons;
        }
      }
      // Setup shipments if they exist (not in summary mode)
      this.setupShipmentsForCompany(companyAdjustment);
    });
  }

  /**
   * Calculate page totals from paginatedAdjustments
   */
  private calculatePageTotals(): void {
    this.pageTotalAmount = this.paginatedAdjustments.reduce(
      (sum, company) => sum + (company.totalAdjustmentsAmount || 0),
      0
    );
    this.pageShipmentCount = this.paginatedAdjustments.reduce(
      (sum, company) => sum + (company.shipmentsCount || 0),
      0
    );
  }

  /**
   * Setup shipment data for display (extracted for reuse in lazy loading)
   */
  private setupShipmentsForCompany(companyAdjustment: any): void {
    if (!companyAdjustment || !companyAdjustment.shipments || companyAdjustment.shipments.length === 0) return;

    try {
      companyAdjustment.shipments.map((shipment: any) => {
        if (!shipment) return; // Skip null/undefined shipments
      // merged shipments
      shipment.isMergedShipment = this.isMergedShipment(shipment);
      shipment.mergedShipmentPopover = shipment.isMergedShipment ? this.getMergedShipmentsChildren(shipment) : '';
      // shipment with unknown tracking numbers assigned to it
      shipment.assignedUnknownShipmentPopover = shipment.hasAssignedUnknownShipment
        ? this.getAssignedUnknownShipments(shipment)
        : '';
      shipment.adjustmentReasons = this.getAdjustmentReasons(shipment);
      shipment.providerImage = shipment.isMultiLeg
        ? this.getProviderImage(shipment.shipmentLegs[0]?.provider, shipment.service)
        : this.getProviderImage(shipment.provider, shipment.service);
      shipment.providerName = shipment.isMultiLeg
        ? this.helpersService.convertShipmentProviderToString(shipment.shipmentLegs[0]?.provider)
        : this.helpersService.convertShipmentProviderToString(shipment.provider);
      shipment.providerCode = this.getProviderCode(shipment.service);
      shipment.statusVerbose = this.helpersService.convertLegacyShipmentStatusEnumToString(shipment.status);
      shipment.trackingStatusVerbose = shipment.trackingStatus
        ? this.helpersService.convertLegacyShipmentStatusEnumToString(shipment.trackingStatus)
        : 'Not set';
      shipment.providerLogoSrc = this.getProviderLogoSrc(shipment.providerCode);

      shipment.hasReturnLabel = this.shipmentsService.isPrintableReturnLabelAvailable(shipment);
      shipment.hasThirdPartyInsurance =
        shipment.rates.thirdPartyInsurance && !_.isEmpty(shipment.rates.thirdPartyInsurance);
      shipment.isCPNC = shipment.provider === ShippingProviders.CANADA_POST && _.includes(shipment.group, 'NC_');
      shipment.senderInfo.fullPhone = this.getPhoneNumber(shipment.senderInfo);
      shipment.senderInfo.formattedCostCenter = _.get(
        shipment.senderInfo.costCenter,
        'name',
        shipment.senderInfo.costCenter
      );
      shipment.recipientInfo.buzzer = _.get(shipment, 'recipientInfo.address.additionalInfo');
      shipment.recipientInfo.fullPhone = this.getPhoneNumber(shipment.recipientInfo);
      this.isDomesticAccShipment =
        (shipment.provider === ShippingProviders.RIVO || shipment.provider === ShippingProviders.MULTILEG) &&
        (shipment.recipientInfo.address.country || '').toUpperCase().includes('CA');

      shipment.updatedRates = [];

      this.rates = this.shipmentsService.rateByProvider(
        shipment.provider,
        shipment.service.name,
        shipment.rates || {}
      );

      if (this.rates) {
        shipment.hasOptions = this.hasOptions(this.rates);
        this.rates.forEach((rate) => {
          if (!_.get(rate, 'amount') && _.get(rate, 'value')) {
            rate.amount = rate.value;
          }
          if (rate.list && rate.list.length > 0) {
            rate.list.forEach((listItem) => {
              if (!_.get(listItem, 'amount') && _.get(listItem, 'value')) {
                listItem.amount = listItem.value;
              }
            });
          }
        });

        this.filterRates(shipment);
        if (shipment.rates.adjustmentCharges && shipment.rates.adjustmentCharges.length > 0) {
          const metadata = shipment.rates.adjustmentCharges.flatMap(
            (adjustmentCharge) => adjustmentCharge.metadata
          );
          const metadataForMatchingTrackingNumber = metadata.find(
            (m) => m.trackingNumber === shipment.rates.trackingNumber
          );

          if (metadataForMatchingTrackingNumber && metadataForMatchingTrackingNumber.adjustedWeight > 0) {
            shipment.adjustedWeight = metadataForMatchingTrackingNumber.adjustedWeight;
            shipment.adjustedUnits = metadataForMatchingTrackingNumber.adjustedUnits;
            shipment.hasAdjustment = true;
          }
        }
      }

      shipment.updatedRates.push(...this.rates);

      this.additionalDetails = {
        reference1: shipment.recipientInfo.reference1,
        reference2: shipment.recipientInfo.reference2,
        additionalNote: shipment.recipientInfo.additionalNote,
      };

      this.noReferences =
        !this.additionalDetails.reference1 &&
        !this.additionalDetails.reference2 &&
        !this.additionalDetails.additionalNote;
      });
    } catch (error) {
      this.logger.error('Error setting up shipments for company', { error, companyAdjustment });
      // Don't throw - just log and continue
    }
  }

  private updateFiltersText(isClearing: boolean = false) {
    if (isClearing) {
      this.filtersText = this.defaultSearchText;
    } else {
      let updatedText = '';
      const fromDate = DateTime.fromJSDate(this.selectedFilters.fromDate).toISO();
      const toDate = DateTime.fromJSDate(this.selectedFilters.toDate).toISO();

      // Date Range
      updatedText += `${fromDate} - ${toDate}`;

      // Charge Status
      if (!_.isEmpty(this.selectedFilters.chargeStatus)) {
        const orderStatus = this.statusItems.find((item) => item.id === this.selectedFilters.chargeStatus);

        if (orderStatus) {
          updatedText += !_.isEmpty(updatedText) ? ', ' : '';
          updatedText += `Status: ${orderStatus.text}`;
        }
      }

      // Tracking Number
      if (!_.isEmpty(this.selectedFilters.trackingNumber)) {
        updatedText += !_.isEmpty(updatedText) ? ', ' : '';
        updatedText += `Order: ${this.selectedFilters.trackingNumber}`;
      }

      this.filtersText = !_.isEmpty(updatedText) ? updatedText : this.defaultSearchText;
    }
  }

  private getAssignedUnknownShipments(shipment) {
    const shipmentWithAssignedUnknown = shipment.hasAssignedUnknownShipment;
    return shipmentWithAssignedUnknown.metadata
      .filter((item) => item.assignedTrackingNumber)
      .filter((item) => item.assignedTrackingNumber !== shipment.tracking)
      .map((item) => ` ${item.assignedTrackingNumber}`);
  }

  private setupCompanyAdjustmentForBatch(companyAdjustment) {
    companyAdjustment.displayName = this.getAccountName(companyAdjustment);

    for (const shipment of companyAdjustment.shipments) {
      shipment.adjustmentReasons = this.getAdjustmentReasons(shipment);
      shipment.adjustmentValue = this.getAdjustmentValue(shipment);
      shipment.provider = this.getProviderName(shipment.provider);
      shipment.selected = true;
    }

    return companyAdjustment;
  }

  private isMergedShipment(shipment) {
    return (
      shipment.rates.adjustmentCharges &&
      shipment.rates.adjustmentCharges.length > 0 &&
      shipment.rates.adjustmentCharges.find((charge) => {
        return (
          charge.metadata &&
          charge.metadata.length > 1 &&
          charge.metadata.filter((item) => item.trackingNumber).length > 1
        );
      })
    );
  }

  private getMergedShipmentsChildren(shipment) {
    const chargeWithMergedShipments = this.isMergedShipment(shipment);

    if (chargeWithMergedShipments) {
      return chargeWithMergedShipments.metadata
        .filter((item) => item.trackingNumber)
        .filter((item) => item.trackingNumber !== shipment.tracking)
        .map((item) => ` ${item.trackingNumber}`);
    } else {
      return '';
    }
  }

  private buildProviderItems(): { id: string; value: string; text: string }[] {
    const items: { id: string; value: string; text: string }[] = [];
    for (const item of ReconProviderItems) {
      if (item.id === ShippingProviders.UNIUNI) {
        // Push a copy with the overridden label — mutating `item.text` in place would rewrite
        // the shared ReconProviderItems entry that /unknown-tracking, /not-invoiced-ma, and
        // /not-invoiced-courier also render, none of which have a PostNL/direct split.
        items.push({ ...item, text: UNIUNI_ALL_LABEL });
        items.push(
          { id: UNIUNI_POSTNL_FILTER_ID, value: UNIUNI_POSTNL_FILTER_ID, text: ShippingProviderNames.POSTNL_UNIUNI },
          { id: UNIUNI_DIRECT_FILTER_ID, value: UNIUNI_DIRECT_FILTER_ID, text: UNIUNI_DIRECT_LABEL }
        );
      } else {
        items.push(item);
      }
    }
    return items;
  }

  // The synthetic sub-filter ids are a UI-only concept: translate them to the real
  // provider_type plus the uniuni_routing param before anything reaches the backend.
  private resolveProviderSelection(selection: string): { provider: string; uniuniRouting?: 'postnl' | 'direct' } {
    if (selection === UNIUNI_POSTNL_FILTER_ID) {
      return { provider: ShippingProviders.UNIUNI, uniuniRouting: 'postnl' };
    }
    if (selection === UNIUNI_DIRECT_FILTER_ID) {
      return { provider: ShippingProviders.UNIUNI, uniuniRouting: 'direct' };
    }
    return { provider: selection };
  }

  private createForm() {
    this.searchForm = this.formBuilder.group({
      chargeStatus: [this.defaultStatus.value, []],
      trackingNumber: [this.trackingNumber || ''],
      provider: ['all'],
      startDate: [undefined],
      endDate: [undefined],
    });
  }

  // SHIPMENTS

  public getProviderImage(provider, service?: any): string {
    return this.providersService.getProviderSmallLogo(provider, service);
  }

  public getProviderLogoSrc(providerCode: string): string {
    if (providerCode === ShippingProviders.COURANT_PLUS) {
      providerCode = 'courantplus-nationex';
    }
    // Purolator Freight artwork only exists on the CDN as a PNG.
    const extension = providerCode === ShippingProviders.FREIGHT_PUROLATOR ? 'png' : 'svg';
    return `${this.CDN_URL}/shared/images/couriers/${providerCode}.${extension}`;
  }

  public pageChanged(pageEvent: PageEvent): void {
    this.itemsPerPage = pageEvent.pageSize;
    this.currentPage = pageEvent.pageIndex + 1;

    this.route.queryParams.subscribe((params) => {
      this.router.navigate(['/reconciliation'], { queryParams: { page: this.currentPage } });
    });

    this.getAdjustments(this.generateQueryData());
  }

  public onPageSizeChange(event: any): void {
    this.itemsPerPage = event.value;
    this.currentPage = 1;
    this.getAdjustments(this.generateQueryData());
  }

  private generateQueryData(): any {
    const searchValues = (this.searchForm || {}).value;
    const startDate = DateTime.fromJSDate(this.range.value.start).setZone(this.defaultTimezone).startOf('day');
    const endDate = DateTime.fromJSDate(this.range.value.end)
      .setZone(this.defaultTimezone)
      .set({ hour: 23, minute: 59, second: 59, millisecond: 0 });
    const providerSelection = this.resolveProviderSelection(searchValues.provider);

    const queryData: any = {
      start_date: startDate.toISO(),
      end_date: endDate.toISO(),
      tracking_number: searchValues.trackingNumber || '',
      charge_status: searchValues.chargeStatus,
      provider: providerSelection.provider,
      items_per_page: this.itemsPerPage,
      page: this.currentPage,
      order_by_value: this.orderByValue,
      order_by_direction: this.sortDescending ? 'DESC' : 'ASC',
      company: this.companyId || '',
      include_admin_charge_details: true,
      is_admin_call: true,
    };
    if (providerSelection.uniuniRouting) {
      queryData.uniuni_routing = providerSelection.uniuniRouting;
    }
    return queryData;
  }

  private surchargeCodeOrNameIncludesFuel = (surcharge: any) => {
    const { code, name } = surcharge;

    if (code && _.isString(code) && code.toLowerCase().includes('fuel')) {
      return true;
    }

    if (name && _.isString(name) && name.toLowerCase().includes('fuel')) {
      return true;
    }

    return false;
  };

  private getPhoneNumber(addressInfo): string {
    const phone = _.get(addressInfo, 'phone.phoneNumber', _.get(addressInfo, 'phone', ''));
    const phoneExtension = _.get(addressInfo, 'phone.extension', _.get(addressInfo, 'phoneExtension', ''));

    return phoneExtension && phoneExtension.length > 0 ? `${phone} x${phoneExtension}` : phone;
  }

  private hasOptions(rates) {
    let hasOptions = false;

    for (const rate of rates) {
      if (rate.label === 'options' && rate.list && rate.list.length > 0) {
        hasOptions = true;
      }
    }

    return hasOptions;
  }

  private filterRates(shipment) {
    if (!_.isEmpty(shipment.service.thirdParty)) {
      _.remove(this.rates, { label: 'regular price' });
      _.remove(this.rates, { label: 'taxes' });
      _.remove(this.rates, { label: 'total' });
      _.remove(this.rates, { label: 'discount' });

      const surchargeIndex = _.findIndex(this.rates, { label: 'surcharges' });

      if (surchargeIndex) {
        _.remove(this.rates[surchargeIndex].list, this.surchargeCodeOrNameIncludesFuel);
      }
    }

    // Remove null discounts
    _.remove(this.rates, { label: 'discount', amount: null });
  }
}
